import { Observable, of, defer } from "rxjs"
import { switchMap, map, catchError, filter } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { Router } from "../../../../server/router"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    CLOSE_MODIFIER_FORM_CLICKED,
    DELETE_MODIFIER_CLICKED,
    MODIFIER_FORM_SUBMITTED,
    MODIFIER_LOCATION_SAVE_REQUESTED,
    MODIFIER_SAVE_REQUESTED,
    modifierLocationSaveFailure,
    modifierLocationSaveRequested,
    modifierLocationSaveSuccess,
    modifierSaveFailure,
    modifierSaveRequested,
    modifierSaveSuccess,
} from "./actions"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import type { ModifierFormItem } from "./ModifierForm"
import {
    createActionItemsAndCategoriesAgendaModifiersLoaded,
    invalidateMenuData,
    ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
    MENU_DATA_INVALIDATED,
    shouldRefetchMenuDataEntity,
} from "../actions"
import { createLoadingEnded, createLoadingStarted } from "../../../filters/actions"
import { getHttpHeaders } from "../../../auth/selectors"
import { fetchModifiers, modifierDelete, modifierLocationSave, modifierSave } from "./request"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import type {
    CategoryModifier,
    ModifierLocationSaveRequestData,
    ModifierSaveRequestData,
} from "../../../../graphql/types"
import { getModifierById, shouldAddNewModifierToModifierGroup } from "./selectors"
import { addModifierToModifierGroupData, redirectBackToModifierGroupDetail } from "./utils"
import { createActionLoadingError } from "../../../data/actions"
import { shouldDownloadItemsAndCategoriesData } from "../selectors"
import type { ModifierGroupFormItem } from "../modifierGroups/ModifierGroupForm"
import { getModifierGroupFormData } from "../modifierGroups/selectors"
import { modifierGroupDataUpdated } from "../modifierGroups/actions"
import { SEND_CSV_FILE_SUCCESS } from "../../../../api/actions"
import { LOAD_AUTH_LOCATIONS_SUCCESS } from "../../../layouts/header/actions"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"

export const modifierFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("modifierFormSubmittedEpics - form submitted", {
                data: action.payload,
            })

            if (!action.payload.oldValue) {
                const modifier: ModifierFormItem = action.payload.newValue
                const data: ModifierSaveRequestData = {
                    id: deps.idgen.next(),
                    name: modifier.name,
                    kitchenName: modifier.kitchenName,
                    isActive: modifier.isActive,
                    isCreating: true,
                }

                // No redirect here: createModifierSubmittedRouterEpics already routes this very
                // action. Pushing the same url twice in one tick makes Next cancel the in-flight
                // route change, and every later navigation then fails with "Cancel rendering
                // route" - routeChangeComplete stops firing, so _app never dispatches URL_CHANGED
                // again and redux' currentPath stays frozen. FGR3-10088.
                return [modifierSaveRequested(data)]
            } else {
                const selectedLocationId = getSelectedLocationOverrideId(deps.getState())

                if (selectedLocationId) {
                    const modifier: ModifierFormItem = action.payload.newValue
                    const data: ModifierLocationSaveRequestData = {
                        id: modifier.modifierId,
                        locationId: selectedLocationId,
                        isActive: modifier.isActive,
                    }
                    return [modifierLocationSaveRequested(data, selectedLocationId)]
                } else {
                    const modifier: ModifierFormItem = action.payload.newValue
                    const data: ModifierSaveRequestData = {
                        id: modifier.modifierId,
                        name: modifier.name,
                        kitchenName: modifier.kitchenName,
                        isActive: modifier.isActive,
                        isCreating: false,
                    }
                    return [modifierSaveRequested(data)]
                }
            }
        })
    )

export const modifierSaveRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_SAVE_REQUESTED),
        switchMap((action: Action) => {
            const data: ModifierSaveRequestData = {
                ...action.payload.data,
                auditData: constructAuditData(deps, true),
            }
            return defer(() => modifierSave(getHttpHeaders(deps.getState()), data)).pipe(
                switchMap(() => {
                    const payload: ModifierSaveRequestData = action.payload.data
                    const originModifier = getModifierById(deps.getState(), payload.id)
                    const modifier: CategoryModifier = {
                        id: payload.id,
                        name: payload.name,
                        isActive: payload.isActive,
                        kitchenName: payload.kitchenName,
                        addedToGroups: originModifier ? originModifier.addedToGroups : [],
                    }

                    if (shouldAddNewModifierToModifierGroup(deps.getState())) {
                        const oldModifierGroupData = getModifierGroupFormData(deps.getState())

                        if (!oldModifierGroupData) {
                            return []
                        }

                        const newModifierGroupData: ModifierGroupFormItem = addModifierToModifierGroupData(
                            oldModifierGroupData,
                            action.payload.data
                        )
                        return [
                            modifierSaveSuccess(modifier),
                            modifierGroupDataUpdated(oldModifierGroupData, newModifierGroupData),
                        ]
                    }

                    // Creating a modifier cannot affect groups; renaming one makes their copy stale.
                    if (payload.isCreating) {
                        return [modifierSaveSuccess(modifier)]
                    }

                    return [modifierSaveSuccess(modifier), invalidateMenuData(["modifierGroups", "itemDetails"])]
                }),
                catchError(error => of(modifierSaveFailure(error)))
            )
        })
    )

export const modifierLocationSaveRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_LOCATION_SAVE_REQUESTED),
        switchMap((action: Action) => {
            const data: ModifierLocationSaveRequestData = {
                ...action.payload.data,
                auditData: constructAuditData(deps, true),
            }
            return defer(() => modifierLocationSave(getHttpHeaders(deps.getState()), data)).pipe(
                switchMap(response => {
                    if (response) {
                        return [modifierLocationSaveSuccess(action.payload.data, action.payload.locationId)]
                    }

                    return []
                }),
                catchError(error => of(modifierLocationSaveFailure(error)))
            )
        })
    )

export const deleteModifierClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_MODIFIER_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteModifierClickedEpics - form submitted", {
                data: action.payload,
            })

            return defer(() =>
                modifierDelete(
                    getHttpHeaders(deps.getState()),
                    action.payload.modifierId,
                    constructAuditData(deps, true)
                )
            ).pipe(
                switchMap(() => [invalidateMenuData(["modifierGroups", "itemDetails"])]),
                catchError(error => of(createActionLoadingError(error)))
            )
        })
    )

export const createModifierSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_FORM_SUBMITTED, CLOSE_MODIFIER_FORM_CLICKED, DELETE_MODIFIER_CLICKED),
        switchMap(() => {
            const isAddingNewModifierToModifierGroup = shouldAddNewModifierToModifierGroup(deps.getState())

            if (isAddingNewModifierToModifierGroup) {
                redirectBackToModifierGroupDetail(deps.getState())
                return []
            } else if (!isAddingNewModifierToModifierGroup) {
                Router.push("/menu/modifiers")
            }

            return []
        })
    )

export const mountedModifiersLoadingEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            SEND_CSV_FILE_SUCCESS,
            LOAD_AUTH_LOCATIONS_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "modifiers")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        map(() => createLoadingStarted("modifiers"))
    )

export const mountedModifiersEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            SEND_CSV_FILE_SUCCESS,
            LOAD_AUTH_LOCATIONS_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "modifiers")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        switchMap(() =>
            defer(() => fetchModifiers(getHttpHeaders(deps.getState()))).pipe(
                switchMap(result => {
                    if (!result) {
                        return [createLoadingEnded("modifiers")]
                    }

                    return [
                        createLoadingEnded("modifiers"),
                        createActionItemsAndCategoriesAgendaModifiersLoaded(result),
                    ]
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        )
    )
export default [
    modifierLocationSaveRequestedEpics,
    modifierSaveRequestedEpics,
    modifierFormSubmittedEpics,
    createModifierSubmittedRouterEpics,
    deleteModifierClickedEpics,
    mountedModifiersLoadingEpic,
    mountedModifiersEpic,
]
